import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message
      });

      setIsSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: "", email: "", phone: "", message: "" });
    setIsSubmitted(false);
  };

  return (
    <div data-testid="contact-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-copper text-sm tracking-[0.3em] uppercase mb-4 font-body">Get in Touch</p>
          <h1 
            data-testid="contact-title"
            className="font-heading text-4xl md:text-6xl mb-6"
          >
            <span className="copper-text">Contact</span>
          </h1>
          <p className="font-body text-gray-400 max-w-2xl mx-auto">
            Have questions about custom orders or want to discuss a project? 
            I'd love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h2 className="font-heading text-2xl mb-8 text-white">Let's Connect</h2>
            
            <div className="space-y-6 mb-12">
              <div 
                data-testid="contact-email"
                className="flex items-start gap-4 p-4 border border-white/5 hover:border-copper/30 transition-colors"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-copper/50">
                  <Mail className="text-copper" size={20} />
                </div>
                <div>
                  <h3 className="font-body text-white mb-1">Email</h3>
                  <a 
                    href="mailto:kairoscopper@gmail.com"
                    className="font-body text-gray-400 hover:text-copper transition-colors"
                  >
                    kairoscopper@gmail.com
                  </a>
                </div>
              </div>

              <div 
                data-testid="contact-phone"
                className="flex items-start gap-4 p-4 border border-white/5 hover:border-copper/30 transition-colors"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-copper/50">
                  <Phone className="text-copper" size={20} />
                </div>
                <div>
                  <h3 className="font-body text-white mb-1">Phone</h3>
                  <p className="font-body text-gray-400">
                    Contact via form or email
                  </p>
                </div>
              </div>

              <div 
                data-testid="contact-location"
                className="flex items-start gap-4 p-4 border border-white/5 hover:border-copper/30 transition-colors"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-copper/50">
                  <MapPin className="text-copper" size={20} />
                </div>
                <div>
                  <h3 className="font-body text-white mb-1">Location</h3>
                  <p className="font-body text-gray-400">
                    Custom orders shipped worldwide
                  </p>
                </div>
              </div>
            </div>

            {/* FAQ */}
            <div className="bg-white/[0.02] border border-white/5 p-6">
              <h3 className="font-heading text-lg mb-4 text-white">Common Questions</h3>
              <div className="space-y-4">
                <div>
                  <p className="font-body text-copper text-sm mb-1">How long does an order take?</p>
                  <p className="font-body text-gray-400 text-sm">
                    Depending on complexity, 1-3 weeks from approval.
                  </p>
                </div>
                <div>
                  <p className="font-body text-copper text-sm mb-1">Do you ship internationally?</p>
                  <p className="font-body text-gray-400 text-sm">
                    Yes! Custom orders can be shipped worldwide.
                  </p>
                </div>
                <div>
                  <p className="font-body text-copper text-sm mb-1">Can I request revisions?</p>
                  <p className="font-body text-gray-400 text-sm">
                    Absolutely! We discuss the design before starting.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white/[0.02] border border-white/5 p-8">
            {isSubmitted ? (
              <div data-testid="success-message" className="text-center py-12">
                <div className="w-20 h-20 mx-auto mb-6 border border-copper flex items-center justify-center">
                  <CheckCircle className="text-copper" size={40} />
                </div>
                <h2 className="font-heading text-2xl mb-4 text-white">Message Sent!</h2>
                <p className="font-body text-gray-400 mb-8">
                  Thank you for reaching out. I'll get back to you as soon as possible.
                </p>
                <Button
                  data-testid="send-another-btn"
                  onClick={resetForm}
                  variant="outline"
                  className="border-copper text-copper hover:bg-copper hover:text-white font-body text-sm tracking-widest uppercase px-8 py-4"
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <h2 className="font-heading text-2xl mb-6 text-white">Send a Message</h2>
                
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="font-body text-sm text-gray-400">
                      Your Name *
                    </Label>
                    <Input
                      id="name"
                      data-testid="contact-input-name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter your name"
                      className="mt-2 bg-transparent border-white/20 focus:border-copper text-white placeholder:text-gray-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="font-body text-sm text-gray-400">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="contact-input-email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter your email"
                      className="mt-2 bg-transparent border-white/20 focus:border-copper text-white placeholder:text-gray-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="font-body text-sm text-gray-400">
                      Phone (Optional)
                    </Label>
                    <Input
                      id="phone"
                      data-testid="contact-input-phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Enter your phone number"
                      className="mt-2 bg-transparent border-white/20 focus:border-copper text-white placeholder:text-gray-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="font-body text-sm text-gray-400">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="contact-input-message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell me about your project or ask any questions..."
                      rows={5}
                      className="mt-2 bg-transparent border-white/20 focus:border-copper text-white placeholder:text-gray-500 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={isSubmitting}
                    className="w-full bg-copper hover:bg-copper-light text-white font-body text-sm tracking-widest uppercase py-6 transition-all duration-300"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Send Message
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
